<template>
  <Teleport to="body">
    <div class="ps-toaster" role="region" aria-label="Notifications">
      <TransitionGroup name="ps-toast">
        <div
          v-for="item in toastState.items"
          :key="item.id"
          class="ps-toast"
          :class="`ps-toast--${item.type}`"
          role="status"
          aria-live="polite"
        >
          <v-icon class="ps-toast__icon" :icon="iconFor(item.type)" size="19" />

          <span class="ps-toast__text">{{ item.text }}</span>

          <span v-if="item.count > 1" class="ps-toast__count">{{ item.count }}</span>

          <button
            v-if="item.action"
            type="button"
            class="ps-toast__action"
            @click="runAction(item)"
          >
            {{ item.action.label }}
          </button>

          <button
            type="button"
            class="ps-toast__close"
            aria-label="Dismiss notification"
            @click="dismissToast(item.id)"
          >
            <v-icon icon="mdi-close" size="15" />
          </button>
        </div>
      </TransitionGroup>
    </div>
  </Teleport>
</template>

<script setup>
import { toastState, dismissToast } from '@/services/ui/toast'

const ICONS = {
  success: 'mdi-check-circle-outline',
  error: 'mdi-alert-circle-outline',
  warning: 'mdi-alert-outline',
  info: 'mdi-information-outline',
  default: 'mdi-bell-outline',
}

const iconFor = type => ICONS[type] || ICONS.default

const runAction = item => {
  dismissToast(item.id)
  item.action?.onClick?.()
}
</script>

<style scoped>
.ps-toaster {
  position: fixed;
  left: 50%;
  transform: translateX(-50%);
  /* Clears the bottom navigation; the nav is 64px plus whatever the handset
     reserves for its gesture bar. */
  bottom: calc(84px + env(safe-area-inset-bottom));
  z-index: var(--ps-z-toast);
  display: flex;
  flex-direction: column;
  gap: var(--ps-space-2);
  width: min(calc(100vw - 32px), 440px);
  pointer-events: none;
}

.ps-toast {
  pointer-events: auto;
  display: flex;
  align-items: center;
  gap: var(--ps-space-2);
  padding: 11px 12px 11px 14px;
  border-radius: var(--ps-radius);
  background: var(--ps-ink);
  border: 1px solid var(--ps-ink-border);
  box-shadow: 0 12px 32px rgba(0, 0, 0, 0.32);
  color: var(--ps-text-on-ink);
  font-size: 14px;
  line-height: 1.4;
}

.ps-toast__icon {
  flex-shrink: 0;
}

.ps-toast--success .ps-toast__icon {
  color: var(--ps-success);
}
.ps-toast--error .ps-toast__icon {
  color: var(--ps-error);
}
.ps-toast--warning .ps-toast__icon {
  color: var(--ps-warning);
}
.ps-toast--info .ps-toast__icon {
  color: var(--ps-info);
}
.ps-toast--default .ps-toast__icon {
  color: var(--ps-accent);
}

.ps-toast__text {
  flex: 1;
  min-width: 0;
  overflow-wrap: anywhere;
}

.ps-toast__count {
  flex-shrink: 0;
  min-width: 20px;
  height: 20px;
  padding: 0 6px;
  border-radius: var(--ps-radius-pill);
  background: rgba(255, 255, 255, 0.12);
  font-size: 11.5px;
  font-weight: 700;
  line-height: 20px;
  text-align: center;
}

.ps-toast__action {
  flex-shrink: 0;
  padding: 6px 12px;
  border: none;
  border-radius: var(--ps-radius-pill);
  background: var(--ps-accent);
  color: #1a1a1a;
  font-family: inherit;
  font-size: 13px;
  font-weight: 700;
  cursor: pointer;
}

.ps-toast__close {
  flex-shrink: 0;
  width: 26px;
  height: 26px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: none;
  border-radius: 50%;
  background: transparent;
  color: rgba(255, 255, 255, 0.5);
  cursor: pointer;
}

.ps-toast__close:hover {
  background: rgba(255, 255, 255, 0.08);
  color: var(--ps-text-on-ink);
}

.ps-toast__action:focus-visible,
.ps-toast__close:focus-visible {
  outline: 2px solid var(--ps-accent);
  outline-offset: 2px;
}

.ps-toast-enter-active,
.ps-toast-leave-active {
  transition: opacity var(--ps-duration) var(--ps-ease), transform var(--ps-duration) var(--ps-ease);
}

.ps-toast-enter-from,
.ps-toast-leave-to {
  opacity: 0;
  transform: translateY(10px) scale(0.97);
}

.ps-toast-move {
  transition: transform var(--ps-duration) var(--ps-ease);
}
</style>
